import React, { useState, useEffect, useRef, useCallback } from 'react';
import './LazyImage.css';

interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  placeholder?: string;
  width?: number;
  height?: number;
  quality?: number; // 图片质量 1-100
  priority?: boolean; // 是否优先加载
}

// 图片缓存
const imageCache = new Map<string, string>();

// 预加载图片
const preloadImage = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = src;
  });
};

// 优化图片 URL（添加参数）
const optimizeImageUrl = (
  src: string, 
  width?: number, 
  height?: number, 
  quality?: number
): string => {
  // 如果是 Supabase URL，添加转换参数
  if (src.includes('supabase')) {
    const url = new URL(src);
    if (width) url.searchParams.set('width', width.toString());
    if (height) url.searchParams.set('height', height.toString());
    if (quality) url.searchParams.set('quality', quality.toString());
    return url.toString();
  }
  return src;
};

// 检测 WebP 支持
const supportsWebP = (() => {
  const canvas = document.createElement('canvas');
  if (canvas.getContext && canvas.getContext('2d')) {
    return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  }
  return false;
})();

const LazyImage: React.FC<LazyImageProps> = ({ 
  src, 
  alt, 
  className = '',
  placeholder = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 160 160"%3E%3Crect fill="%23ffeef8" width="160" height="160"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%23FFB6C1" font-size="14" font-weight="600"%3E加载中...%3C/text%3E%3C/svg%3E',
  width,
  height,
  quality = 80,
  priority = false
}) => {
  const [imageSrc, setImageSrc] = useState(placeholder);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isError, setIsError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // 优化图片 URL
  const optimizedSrc = useCallback(() => {
    // 检查缓存
    if (imageCache.has(src)) {
      return imageCache.get(src)!;
    }

    let optimized = optimizeImageUrl(src, width, height, quality);
    
    // 如果支持 WebP 且不是 placeholder，尝试使用 WebP
    if (supportsWebP && !src.includes('placeholder') && !src.includes('data:image')) {
      // 对于 Supabase，可以添加 format=webp 参数
      if (optimized.includes('supabase')) {
        const url = new URL(optimized);
        url.searchParams.set('format', 'webp');
        optimized = url.toString();
      }
    }

    imageCache.set(src, optimized);
    return optimized;
  }, [src, width, height, quality]);

  useEffect(() => {
    // 如果是优先加载，立即加载图片
    if (priority) {
      const optimized = optimizedSrc();
      preloadImage(optimized).then(() => {
        setImageSrc(optimized);
      }).catch(() => {
        setIsError(true);
      });
      return;
    }

    // 使用 IntersectionObserver 实现懒加载
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const optimized = optimizedSrc();
            setImageSrc(optimized);
            observerRef.current?.disconnect();
          }
        });
      },
      {
        rootMargin: '100px', // 提前100px开始加载
        threshold: 0.01 // 只要1%可见就开始加载
      }
    );

    if (imgRef.current) {
      observerRef.current.observe(imgRef.current);
    }

    return () => {
      observerRef.current?.disconnect();
    };
  }, [src, priority, optimizedSrc]);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
  }, []);

  const handleError = useCallback(() => {
    setIsError(true);
    // 错误时使用动漫风格的占位图
    setImageSrc('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 160 160"%3E%3Crect fill="%23fff5f7" width="160" height="160"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%23FF6B9D" font-size="14" font-weight="600"%3E加载失败%3C/text%3E%3C/svg%3E');
  }, []);

  return (
    <img
      ref={imgRef}
      src={imageSrc}
      alt={alt}
      className={`lazy-image ${className} ${isLoaded ? 'loaded' : ''} ${isError ? 'error' : ''}`}
      onLoad={handleLoad}
      onError={handleError}
      loading={priority ? 'eager' : 'lazy'} // 使用原生懒加载作为后备
      decoding="async" // 异步解码
      width={width}
      height={height}
    />
  );
};

export default LazyImage;
